import { makeAutoObservable } from 'mobx';
import { ConditionType } from '../../component/form/search-key-input.component';
import apiManage from '../../request';
import { TimeType } from '../../request/api/risk';
import { getRiskTagTextForOrder, normalizeInstantRiskResponse, RiskOrderGroup, RiskOrderMode } from './risk-order-mode';
import { RiskOrder } from '../../models';
import { validateCurrentDate, dateFormat, formatRiskMoney, formatRiskMinutes, formatRiskStatusText } from '../../constants/tool-order';
import moment from 'moment';
import { exportMultipleSheetExcel } from '../../constants/tool.fn';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import LSM from '../../modules/cache/tool-local-storage';
import i18n from '../../i18n';
import GlobalNotification from '../../component/common/snackbar';
import { dateFormat as dateFormat2 } from '../../constants/utils';


export interface RiskOrderListData {
    list: RiskOrder[];
    total: number;
}

class RiskOrderListState {
    /** 云端列表固定使用订单聚合模式。 */
    readonly riskDataMode: RiskOrderMode = 'order';
    loading = false;
    data: RiskOrderListData = {
        list: [],
        total: 0,

    };
    // 搜索条件（高级筛选）
    conditions: ConditionType[] = [];

    // 当前餐厅标识
    stringId: string = '';

    // 分页参数
    page = 1;
    perPage = 20;

    // 排序参数：sortValue 0-不排序 1-降序 2-升序
    sortKey = '';
    sortValue: 0 | 1 | 2 = 0;

    // 时间筛选：后端用 ISO 字符串，默认使用 validateCurrentDate 的时间段
    startTime: string;
    endTime: string;

    // AI 分析对话框状态
    aiAnalysisDialogOpen = false;


    constructor() {
        makeAutoObservable(this);
        const { startTime, endTime } = validateCurrentDate();
        this.startTime = moment(startTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm');
        this.endTime = moment(endTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm');
    }

    // 初始化状态，传入当前餐厅 stringId
    initialize(stringId: string) {
        this.data = {
            list: [],
            total: 0,
        };
        const { startTime, endTime } = validateCurrentDate();
        this.startTime = moment(startTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm');
        this.endTime = moment(endTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm');
        this.conditions = [];
        this.page = 1;
        this.perPage = 20;
        this.sortKey = '';
        this.sortValue = 0;
        this.stringId = stringId;
        this.fetchData();
    }
    setLoading(value: boolean) {
        this.loading = value;
    }

    setData(value: RiskOrderListData) {
        this.data = value;
    }

    setConditions(value: ConditionType[]) {
        this.conditions = [...value];
        // 切换筛选条件时重置页码
        this.page = 1;
        this.fetchData();
    }

    // 设置当前餐厅 stringId，仅在初始化时调用
    setStringId(id: string) {
        this.stringId = id;
    }

    // 设置分页，并重新拉取数据
    changePage(page: number, perPage: number) {
        this.page = page;
        this.perPage = perPage;
        this.fetchData();
    }

    // 设置排序，并重新拉取数据
    changeSort(headCellId: string, currentSortStatus: 0 | 1 | 2) {
        let nextSortValue = currentSortStatus;
        switch (currentSortStatus) {
            case 0:
                nextSortValue = 1;
                break;
            case 1:
                nextSortValue = 2;
                break;
            case 2:
                nextSortValue = 0;
                break;
        }
        this.page = 1;
        // 特殊处理：表头 amount 实际按 origin_amount 字段排序
        this.sortKey = headCellId === 'amount' ? 'origin_amount' : headCellId;
        this.sortValue = nextSortValue;
        this.fetchData();
    }

    // 设置时间范围，并重新拉取数据
    setDateRange(start: Date, end: Date) {
        this.startTime = moment(start).format('YYYY-MM-DD HH:mm');
        this.endTime = moment(end).format('YYYY-MM-DD HH:mm');
        this.page = 1;
        this.fetchData();
    }

    /**
     * 与列表请求一致：排序参数（表头 `amount` 映射为 `origin_amount` 在 `changeSort` 已处理）
     */
    private buildSortParamsForCloudList() {
        const sortParams: Record<string, string | number> = {};
        if (this.sortValue === 0 || !this.sortKey) {
            sortParams.sort_key = 'open_time';
            sortParams.sort_value = -1;
        } else {
            sortParams.sort_key = this.sortKey;
            sortParams.sort_value = this.sortValue === 1 ? -1 : 1;
        }
        return sortParams;
    }

    // 将前端条件转换为接口查询参数
    private buildSearchParams() {
        const params: any = {};
        this.conditions.forEach((item) => {
            if (!item.key || !item.value) return;

            // 风险标签做特殊映射，其它条件按 key 聚合即可
            if (item.key === 'risk_tag') {
                /**
                 * 风险标签筛选映射规则：
                 * 1-5：风控类型，对应 risk_types 1-5
                 * 6：事件类型-快速关闭订单，对应 event_types 1
                 * 7：事件类型-取消订单，对应 event_types 2
                 */
                if (item.value === '6') {
                    params.event_types = params.event_types || [];
                    params.event_types.push('1');
                } else if (item.value === '7') {
                    params.event_types = params.event_types || [];
                    params.event_types.push('2');
                } else {
                    params.risk_types = params.risk_types || [];
                    params.risk_types.push(item.value);
                }
                return;
            }

            // 与移动端 condition-filter buildSearchParams 一致：云端列表使用单个 status 数字参数
            if (item.key === 'status') {
                const num = Number.parseInt(String(item.value), 10);
                if (Number.isFinite(num) && num >= 0 && num <= 2) {
                    params.status = num;
                }
                return;
            }

            // 其它字段直接按 key 聚合成数组
            const key = item.key;
            params[key] = params[key] || [];
            params[key].push(item.value);
        });
        return params;
    }

    async fetchData(stringId?: string) {
        if (stringId) {
            this.setStringId(stringId);
        }
        if (!this.stringId) return;

        this.setLoading(true);
        const searchParams = this.buildSearchParams();
        const sortParams = this.buildSortParamsForCloudList();

        const res = await apiManage.get_cloud_restaurant_risk(this.stringId, {
            pagination_page: this.page,
            pagination_per_page: this.perPage,
            time_type: TimeType.Custom,
            start_time: this.startTime,
            end_time: this.endTime,
            mode: this.riskDataMode,
            ...sortParams,
            ...searchParams,
        });

        if (res && !res.error) {
            const { dbResults, totalCount } = res?.[0] || {};
            const normalized = normalizeInstantRiskResponse({ riskOrders: dbResults }, this.riskDataMode);
            this.setData({ list: normalized.orders, total: totalCount?.[0]?.count ?? normalized.orders.length });
        }
        this.setLoading(false);
    }

    /**
     * 按当前时间、筛选、排序分页请求直至拉齐 `total` 条，再导出为 Excel（列表区仍为分页展示）
     */
    async downloadExcel() {
        if (!this.stringId) {
            GlobalNotification.error(i18n.t('general_text_no_data'));
            return;
        }
        const total = this.data.total;
        if (total <= 0) {
            GlobalNotification.error(i18n.t('general_text_no_data'));
            return;
        }
        const searchParams = this.buildSearchParams();
        const sortParams = this.buildSortParamsForCloudList();
        const pageSize = 200;
        const allOrders: RiskOrder[] = [];
        let page = 1;
        // 多页拼接，与当前列表查询条件完全一致
        while (allOrders.length < total) {
            const res = await apiManage.get_cloud_restaurant_risk(this.stringId, {
                pagination_page: page,
                pagination_per_page: pageSize,
                time_type: TimeType.Custom,
                start_time: this.startTime,
                end_time: this.endTime,
                mode: this.riskDataMode,
                ...sortParams,
                ...searchParams,
            });
            if (!res || res.error) {
                GlobalNotification.error(i18n.t('general_text_operate_failure'));
                return;
            }
            const { dbResults } = (res as any)?.[0] || {};
            const rawBatch = (dbResults ?? []) as RiskOrder[];
            if (rawBatch.length === 0) {
                break;
            }
            const normalized = normalizeInstantRiskResponse({ riskOrders: rawBatch }, this.riskDataMode);
            allOrders.push(...normalized.orders);
            if (rawBatch.length < pageSize) {
                break;
            }
            page += 1;
            if (page > 500) {
                break;
            }
        }
        const orders = allOrders;
        if (orders.length === 0) {
            GlobalNotification.error(i18n.t('general_text_no_data'));
            return;
        }

        // 云端风险订单只有一个Sheet
        const sheetData: Array<{ sheetName?: string; data: string[][]; wpx: Array<{ wpx: number }> }> = [];

        const riskOrderSheet = {
            sheetName: i18n.t('intelligent_risk_control_page_tab_cloud_order_list'),
            data: [
                [
                    i18n.t('business_analysis_page_order_account'), // 订单号
                    i18n.t('business_analysis_page_table_name'), // 桌台
                    i18n.t('immediate_number_people'), // 人数
                    i18n.t('intelligent_risk_control_page_column_dish_count'), // 菜品数量
                    i18n.t('intelligent_risk_control_page_column_server'), // 服务员
                    i18n.t('immediate_table_opening_time'), // 开台时间
                    i18n.t('immediate_statement_time'), // 结账时间
                    i18n.t('intelligent_risk_control_page_column_amount_original_actual'), // 原价/实收
                    i18n.t('intelligent_risk_control_page_column_discount_amount'), // 优惠金额
                    i18n.t('intelligent_risk_control_page_column_refund_count'), // 退菜数量
                    i18n.t('intelligent_risk_control_page_column_refund_amount'), // 退菜金额
                    i18n.t('intelligent_risk_control_page_column_avg_dish_count'), // 人均菜品数
                    i18n.t('intelligent_risk_control_page_column_duration_minutes'), // 用餐时长
                    i18n.t('intelligent_risk_control_page_filter_risk_tag'), // 风险标签
                    i18n.t('restaurant_info_page_status'), // 状态
                ]
            ],
            wpx: [
                { wpx: 120 }, // 订单号
                { wpx: 100 }, // 桌台
                { wpx: 80 },  // 人数
                { wpx: 100 }, // 菜品数量
                { wpx: 100 }, // 服务员
                { wpx: 150 }, // 开台时间
                { wpx: 150 }, // 结账时间
                { wpx: 150 }, // 原价/实收
                { wpx: 100 }, // 优惠金额
                { wpx: 100 }, // 退菜数量
                { wpx: 100 }, // 退菜金额
                { wpx: 100 }, // 人均菜品数
                { wpx: 100 }, // 用餐时长
                { wpx: 150 }, // 风险标签
                { wpx: 100 }, // 状态
            ]
        };

        // 填充风险订单数据
        riskOrderSheet.data = riskOrderSheet.data.concat(
            orders.map((order) => {
                return [
                    String(order.order_head_id || '-'),
                    order.table_name || '-',
                    String(order.people_count ?? '-'),
                    String(order.dish_count ?? '-'),
                    order.operator_name || '-',
                    order.open_time ? dateFormat(order.open_time, 'HH:mm DD/MM/YYYY') : '-',
                    order.close_time ? dateFormat(order.close_time, 'HH:mm DD/MM/YYYY') : '-',
                    `${formatRiskMoney(order.origin_amount)}/${formatRiskMoney(order.actual_amount)}`,
                    formatRiskMoney(order.discount_amount),
                    String(order.refund_dish_count ?? '-'),
                    formatRiskMoney(order.refund_amount),
                    String(order.avg_dish_per_person ?? '-'),
                    formatRiskMinutes(order.dining_duration_min),
                    getRiskTagTextForOrder(order as RiskOrderGroup),
                    i18n.t(formatRiskStatusText(order.orderStatus ?? order.risk_status ?? order.status ?? order.event_status)),
                ];
            })
        );

        sheetData.push(riskOrderSheet);

        // 生成文件名：餐厅名称 + 时间范围 + 导出时间
        const DEFAULT_RESTAURANT = LSM.get(LOCAL_STORAGE_KEYS.DEFAULT_RESTAURANT) || {};
        const startDateStr = dateFormat(this.startTime, 'YYYY-MM-DD');
        const endDateStr = dateFormat(this.endTime, 'YYYY-MM-DD');
        const exportDateTime = dateFormat2(new Date().toISOString(), 'YYYY-MM-DD HH-mm');
        const fileName = `${i18n.t('intelligent_risk_control_page_text_restaurant_risk_control')} ${DEFAULT_RESTAURANT.nickname || ''} ${startDateStr}~${endDateStr} ${exportDateTime}`;

        // 导出Excel
        exportMultipleSheetExcel(fileName, sheetData);
    }
    /**
     * 打开 AI 分析对话框。
     * 与菜品分析等页一致：先校验餐厅授权点 AU1030，未通过则提示无权限且不打开弹窗。
     */
    async openAiAnalysisDialog() {
        if (!this.stringId) {
            GlobalNotification.error(i18n.t('general_text_no_data'));
            return;
        }
        const warrantResponse = await apiManage.check_warrant_code({
            string_id: this.stringId,
            code: 'AU1030',
        });
        if (warrantResponse?.error || !warrantResponse.passedWarrant?.length) {
            GlobalNotification.error(i18n.t('restaurant_warrant_page_text_no_auth'));
            return;
        }
        this.aiAnalysisDialogOpen = true;
    }

    /** 关闭 AI 分析对话框 */
    closeAiAnalysisDialog() {
        this.aiAnalysisDialogOpen = false;
    }
}

const riskOrderListState = new RiskOrderListState();

export default riskOrderListState;
